<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import axios from 'axios';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

const page = usePage();
const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const announcement = computed(() => page.props.announcementModal);
const dismissed = ref(false);

// No date in the key: unlike inline banners, a dismissed announcement never
// comes back. The server also filters by recorded dismissals (banner_stats),
// so this localStorage flag is just a fast client-side guard.
const storageKey = (id) => `announcement:dismissed:${id}`;

const visible = computed(() => {
    const a = announcement.value;
    if (!a || dismissed.value) return false;
    try {
        if (localStorage.getItem(storageKey(a.id))) return false;
    } catch (e) { /* localStorage unavailable: still show, server filter applies */ }
    return true;
});

const paragraphs = computed(() =>
    (announcement.value?.body || '')
        .split(/\r?\n+/)
        .map((p) => p.trim())
        .filter(Boolean)
);

function dismiss() {
    const a = announcement.value;
    if (!a) return;
    dismissed.value = true;
    try {
        localStorage.setItem(storageKey(a.id), '1');
    } catch (e) { /* ignore */ }
    if (a.dismiss_url) {
        axios.post(a.dismiss_url).catch(() => {});
    }
}
</script>

<template>
    <Dialog
        v-if="announcement"
        :visible="visible"
        @update:visible="(value) => { if (!value) dismiss(); }"
        modal
        :header="$t(announcement.title)"
        :style="{ width: '560px' }"
        :breakpoints="{ '640px': '92vw' }"
    >
        <div class="flex flex-col gap-3">
            <p v-for="(paragraph, index) in paragraphs" :key="index" class="m-0 leading-relaxed">
                {{ $t(paragraph) }}
            </p>
        </div>
        <template #footer>
            <Button :label="$t('I understand')" severity="primary" @click="dismiss" />
        </template>
    </Dialog>
</template>
